'use client';

import { useEffect, useMemo, PropsWithChildren, useCallback, useRef, useState } from 'react';

import {
  AssistantRuntimeProvider,
  type AssistantCloud,
  Tools,
  useAui,
  useCloudThreadListAdapter,
  useRemoteThreadListRuntime,
  useRuntimeAdapters,
} from '@assistant-ui/react';
import { useChatRuntime } from '@assistant-ui/react-ai-sdk';
import { useLocale } from 'next-intl';
import { useLatest } from 'react-use';

import { credentialToolNames, useHitl } from '@/components/assistant-ui/hitl/hitl-provider';
import { useConfigValue, useTenantName } from '@/components/providers/config-provider';
import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { useDevTools } from '@/components/providers/dev-tools-provider';
import type { Platform } from '@/components/providers/platform-provider';
import { useStorageAccess } from '@/components/providers/storage-access-provider';
import { useCrossTabThreadSync } from '@/hooks/use-cross-tab-thread-sync';
import { useDeletionConfirmation } from '@/hooks/use-deletion-confirmation';
import { useDictationAdapter } from '@/hooks/use-dictation-adapter';
import { useHistoryLock } from '@/hooks/use-history-lock';
import { useResponseInterruption } from '@/hooks/use-response-interruption';
import { useResponseTracking, ResponseTrackingContext } from '@/hooks/use-response-tracking';
import { useResumableStreamDiscovery } from '@/hooks/use-resumable-stream-discovery';
import { useSafePostHog } from '@/hooks/use-safe-posthog';
import { useServerThreadTitleSync } from '@/hooks/use-server-thread-title-sync';
import { useSessionSignals } from '@/hooks/use-session-signals';
import { siteAccessHeartbeatUrl, useSiteAccessHeartbeat } from '@/hooks/use-site-access-heartbeat';
import { useThreadPersistence } from '@/hooks/use-thread-persistence';
import { useThreadStatusFeed } from '@/hooks/use-thread-status-feed';
import { useTrackEvent } from '@/hooks/use-track-event';
import { useTtsInterruption } from '@/hooks/use-tts-interruption';
import { useUntitledThreadRetitle } from '@/hooks/use-untitled-thread-retitle';
import { AssistantCloudLocal } from '@/lib/assistant-cloud-local';
import type { AnalyticsChatTransport } from '@/lib/assistant-cloud-local/analytics-chat-transport';
import {
  createChatTransport,
  type HitlTransportBindings,
} from '@/lib/assistant-cloud-local/create-chat-transport';
import { AssistantCloudServer } from '@/lib/assistant-cloud-server';
import { chatAgentUrl as agentUrlFor } from '@/lib/chat-agent-url';
import {
  CHAT_ANALYTICS_EVENTS,
  NETWORK_ERROR_TO_ABORT_REASON,
  type AbortReason,
} from '@/lib/constants/post-message-types';
import { checkSession } from '@/lib/guest-auth/browser-client';
import { ensureKey } from '@/lib/guest-auth/ensure-history-key';
import { createTenantThreadResumableStorage } from '@/lib/hitl/resumable-storage';
import { lastAssistantMessageIsCompleteWithToolCallsOrApprovalResponses } from '@/lib/hitl/send-automatically-when';
import { PiiResultChannelContext, type PiiResultListener } from '@/lib/pii/pii-result-channel';
import { PiiRefusalError } from '@/lib/pii/server-pii';
import { withCheckedHistory } from '@/lib/pii/unchecked-messages';
import { readBootThreadId } from '@/lib/sync/store';
import { useThreadStatusStore } from '@/lib/thread-status/store';
import { ElevenLabsSpeechAdapter, TTS_PROVIDER } from '@/lib/tts/elevenlabs-speech-adapter';
import type { TtsConfig } from '@/lib/types/tenant';
import { setDeletionQueueMode } from '@/lib/utils/deletion-queue';
import { generateMessageId, popServerMessageId } from '@/lib/utils/message';
import { getNetworkErrorType, isBrowserConnectivityError } from '@/lib/utils/network-errors';
import { resolveResourceId } from '@/lib/utils/resource-id';
import { isWebsiteHistoryMismatch } from '@/lib/website-auth/errors';

function ResponseTracker() {
  useResponseTracking();
  useResponseInterruption();
  useTtsInterruption();
  return null;
}

function ThreadManager() {
  useThreadPersistence();
  useDeletionConfirmation();

  return null;
}

function SessionEffects({
  tenant,
  hasStorageAccess,
  storageChecked,
  heartbeatUrl,
}: {
  tenant: string | null;
  hasStorageAccess: boolean;
  storageChecked: boolean;
  heartbeatUrl?: string;
}) {
  useSessionSignals(tenant);
  useSiteAccessHeartbeat(heartbeatUrl);
  useHistoryLock({ hasStorageAccess, storageChecked, enabled: Boolean(tenant) });
  return null;
}

function ThreadStatusManager() {
  useThreadStatusFeed();
  useServerThreadTitleSync();
  useUntitledThreadRetitle();
  useResumableStreamDiscovery();
  useCrossTabThreadSync();
  return null;
}

export function CloudLocalRuntimeProvider({
  children,
  platform,
}: PropsWithChildren<{ platform?: Platform }>) {
  const posthog = useSafePostHog();
  const trackEvent = useTrackEvent();

  const { hasStorageAccess, storageChecked } = useStorageAccess();
  const { isDebugMode } = useDebugMode();
  const { showDevTools, selectedModel, provider, reasoning } = useDevTools();
  const devToolsRef = useLatest({ showDevTools, selectedModel, provider, reasoning });
  const currentThreadIdRef = useRef<string | null>(null);
  const erroredThreadIdsRef = useRef<Set<string>>(new Set());
  const pendingResponsesRef = useRef<Map<string, number>>(new Map());
  const abortReasonsRef = useRef<Map<string, AbortReason>>(new Map());
  const streamingStartedRef = useRef<Set<string>>(new Set());
  const retriedResponsesRef = useRef<Set<string>>(new Set());
  const lastMessageCountRef = useRef<Map<string, number>>(new Map());
  const locale = useLocale();
  const localeRef = useLatest(locale);

  const dictationAdapter = useDictationAdapter();

  const trackEventRef = useLatest(trackEvent);

  const ttsConfig = useConfigValue<TtsConfig>('meta.tts');
  const speechAdapter = useMemo(() => {
    if (!ttsConfig?.enabled) return undefined;
    if (ttsConfig.provider !== TTS_PROVIDER) return undefined;
    return new ElevenLabsSpeechAdapter({
      voiceId: ttsConfig.voiceId,
      modelId: ttsConfig.modelId,
      voiceSettings: ttsConfig.voiceSettings,
      volume: ttsConfig.volume,
      localeRef,
      threadIdRef: currentThreadIdRef,
      onAnalyticsEvent: (event, properties) => trackEventRef.current({ event, properties }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsConfig?.enabled, ttsConfig?.provider, ttsConfig?.voiceId, ttsConfig?.modelId]);

  const platformRef = useLatest(platform);
  const isDebugModeRef = useLatest(isDebugMode);
  const posthogRef = useLatest(posthog);

  const tenantName = useTenantName();
  const chatAgentUrl = agentUrlFor(tenantName);

  // Server-persistence tenants must never construct the local cloud — its constructor opens
  // IndexedDB; never constructing it is what guarantees zero IDB usage.
  const persistence = useConfigValue<'local' | 'server'>('persistence') ?? 'local';
  const signInEnabled = Boolean(useConfigValue('guestAuth'));
  const siteAccessHeartbeat = useConfigValue<boolean>('siteAccessHeartbeat') === true;
  const sessionTenant = persistence !== 'server' && signInEnabled ? tenantName : null;
  // Route deletions through the in-memory queue (not IndexedDB) for server-persistence tenants.
  // Effect, not the useMemo body: deletions are user-triggered post-mount, so the effect always
  // wins the race, and a side-effect belongs outside render.
  useEffect(() => {
    if (persistence === 'server') setDeletionQueueMode('memory');
  }, [persistence]);
  // Page-load lookup complements host/cross-tab signals and periodic session checks.
  // The store opens without waiting: guest conversations list at once and the history lock
  // re-opens it when the key arrives.
  useEffect(() => {
    if (sessionTenant) void checkSession(sessionTenant);
  }, [sessionTenant]);
  const cloud = useMemo(() => {
    if (persistence !== 'server') {
      return new AssistantCloudLocal({
        ensureKey: owner => ensureKey({ owner, tenant: tenantName }),
      });
    }
    return new AssistantCloudServer({
      agentUrl: chatAgentUrl,
      tenant: tenantName,
      getResourceId: () => resolveResourceId(posthogRef.current?.get_distinct_id()),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistence, chatAgentUrl, tenantName]);

  const hitl = useHitl();
  const hitlRef = useRef<HitlTransportBindings | null>(null);
  const hitlCredentialTools = credentialToolNames(hitl);
  hitlRef.current = hitl.getCredentials
    ? { getCredentials: hitl.getCredentials, credentialToolNames: hitlCredentialTools }
    : null;

  const piiResultChannel = useRef<PiiResultListener | null>(null);

  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;
  const hitlSuppressed = useConfigValue<boolean>('hitlSuppressed') === true;

  // Lazy ref-init: the rule's officially-recommended pattern for one-time
  // construction (the rule's own hint suggests `if (ref.current == null) { ... }`).
  // The transport's option callbacks run at request time and read live values via
  // refs, so the transport never needs to be re-created when inputs change.
  const transportRef = useRef<AnalyticsChatTransport | null>(null);
  if (transportRef.current === null) {
    transportRef.current = createChatTransport({
      chatAgentUrl,
      tenantName,
      localeRef,
      platformRef,
      isDebugModeRef,
      posthogRef,
      trackEventRef,
      currentThreadIdRef,
      erroredThreadIdsRef,
      pendingResponsesRef,
      streamingStartedRef,
      retriedResponsesRef,
      lastMessageCountRef,
      devToolsRef,
      hitlRef,
      hitlSuppressed,
      serverPersistence: persistence === 'server',
      // Resume endpoint requires the resourceId query param — binding is verified server-side.
      resumable: resumableEnabled
        ? {
            storage: createTenantThreadResumableStorage({ tenantName, currentThreadIdRef }),
            resumeApi: (streamId: string) =>
              `${chatAgentUrl}/chat/resume/${streamId}?resourceId=${encodeURIComponent(
                resolveResourceId(posthogRef.current?.get_distinct_id()),
              )}`,
          }
        : undefined,
      onPiiMasked: verdict => piiResultChannel.current?.(verdict),
    });
  }

  const handleError = useCallback(
    (error: unknown) => {
      // The recovery card handles this refusal; it is not a failed service or broken stream.
      if (isWebsiteHistoryMismatch(error)) {
        if (currentThreadIdRef.current) popServerMessageId(currentThreadIdRef.current);
        return;
      }
      // The server refused the turn and saved nothing: the handler takes the message back off the
      // thread, synchronously, before the run settles and history would persist it.
      if (error instanceof PiiRefusalError) {
        piiResultChannel.current?.({ result: error.result, messageId: error.messageId });
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        console.error('[chat-runtime-error]', error);
      }
      const threadId = currentThreadIdRef.current;
      const startTime = threadId ? pendingResponsesRef.current.get(threadId) : undefined;

      const networkErrorType = getNetworkErrorType(error);
      const abortReason = NETWORK_ERROR_TO_ABORT_REASON[networkErrorType];

      const diagnostics = {
        threadId,
        source: 'chat-runtime',
        errorType: networkErrorType,
        abortReason,
        isDocumentHidden:
          typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
        isNetworkOffline: !navigator.onLine,
        requestDurationMs: startTime ? Date.now() - startTime : undefined,
      };

      posthog?.capture(CHAT_ANALYTICS_EVENTS.RESPONSE_FAILED, {
        ...diagnostics,
        // Browsers word an unreachable host differently, so without the text one cause reads
        // as several unrelated problems.
        message: error instanceof Error ? error.message : String(error),
      });

      // Error tracking opens a fresh issue each time a fingerprint rotates and pages us on every
      // new production issue, so reporting a guest's dropped connection turns lost signal into a
      // new bug. The event above keeps the data.
      if (!isBrowserConnectivityError(error)) {
        posthog?.captureException(error, diagnostics);
      }

      if (threadId) {
        erroredThreadIdsRef.current.add(threadId);
        abortReasonsRef.current.set(threadId, abortReason);
        // Thread-status dots are a server-persistence-only feature — gate the write like the feed
        // so non-opted tenants show no error icon (byte-identical sidebar).
        if (persistence === 'server') useThreadStatusStore.getState().markError(threadId);
        // Clean up serverMessageId to prevent memory leak
        popServerMessageId(threadId);
      }

      // IMPORTANT: Do NOT re-throw the error here!
      // If we re-throw, the AI SDK's error handler never completes, and the status
      // remains "streaming" instead of transitioning to "error". This leaves the UI stuck.
      // By returning normally, the AI SDK sets status to "error" and isRunning becomes false.
    },
    [posthog, persistence],
  );

  // Read once, synchronously, before the runtime is constructed: passed to
  // useRemoteThreadListRuntime as initialThreadId, whose __internal_load switches to it —
  // adopting unlisted ids via adapter.fetch and swallowing failures, so a stale/deleted id
  // degrades to the default new thread. Replaces the old post-boot restore effect in
  // useThreadPersistence.
  const [initialThreadId] = useState(() => readBootThreadId() ?? undefined);

  // Compose the framework's remote-thread-list runtime ourselves (instead of useChatRuntime's
  // built-in composition) so the cloud thread list and initialThreadId are supplied by the OUTER
  // runtime with vanilla assistant-ui. useChatRuntime (no cloud) becomes the per-thread
  // runtimeHook: its own nested useRemoteThreadListRuntime detects the surrounding thread-list
  // context and, with allowNesting, degrades to returning just the per-thread runtime (which
  // carries the resume mechanism from the react-ai-sdk patch). This keeps initialThreadId
  // forwarding out of the patch.
  const chatRuntime = useRemoteThreadListRuntime({
    runtimeHook: function RuntimeHook() {
      const history = useRuntimeAdapters()?.history;
      const checkedHistory = useMemo(() => history && withCheckedHistory(history), [history]);
      return useChatRuntime({
        transport: transportRef.current!,
        onError: handleError,
        generateId: generateMessageId,
        // After a tool call completes (incl. HITL human tools resolving via addResult) OR an
        // approval response lands (respondToApproval on a server-side gate), send back so the
        // agent produces its reply. The stock tool-calls predicate alone never auto-sends
        // approval responses.
        sendAutomaticallyWhen: ({ messages }) =>
          lastAssistantMessageIsCompleteWithToolCallsOrApprovalResponses({
            messages,
            humanToolNames: hitl.humanToolNames,
          }),
        adapters: {
          dictation: dictationAdapter,
          speech: speechAdapter,
          history: checkedHistory,
        },
      });
    },
    // AssistantCloudLocal/Server are structural drop-ins for the upstream AssistantCloud but
    // don't nominally extend it; bridge the nominal mismatch without an `any`.
    adapter: useCloudThreadListAdapter({ cloud: cloud as unknown as AssistantCloud }),
    initialThreadId,
  });

  // Backend toolkit entries render locally; the transport does not send their definitions.
  const aui = useAui({ tools: Tools({ toolkit: hitl.toolkit }) });

  const responseTrackingValue = useMemo(
    () => ({
      pendingResponsesRef,
      abortReasonsRef,
      streamingStartedRef,
      retriedResponsesRef,
    }),
    [],
  );

  return (
    <AssistantRuntimeProvider aui={aui} runtime={chatRuntime}>
      <ResponseTrackingContext.Provider value={responseTrackingValue}>
        <PiiResultChannelContext.Provider value={piiResultChannel}>
          <ResponseTracker />
          <ThreadStatusManager />
          <SessionEffects
            tenant={sessionTenant}
            hasStorageAccess={hasStorageAccess}
            storageChecked={storageChecked}
            heartbeatUrl={siteAccessHeartbeat ? siteAccessHeartbeatUrl(tenantName) : undefined}
          />
          {hasStorageAccess && <ThreadManager />}
          {children}
        </PiiResultChannelContext.Provider>
      </ResponseTrackingContext.Provider>
    </AssistantRuntimeProvider>
  );
}
